/**
 * AdminStuckResets — Oct 3 2026
 *
 * Resend is in sandbox mode (SENDER_EMAIL=onboarding@resend.dev), so
 * password-reset emails to any address other than the Resend account
 * owner silently drop. When that happens the backend records a
 * `stuck_password_resets` row so Michael can see who tripped on the
 * flow and WhatsApp them a one-tap sign-in link personally.
 *
 * This whole surface becomes vestigial the day Resend domain
 * verification lands — until then it's a one-stop place to unblock
 * beta testers quickly.
 */
import { useEffect, useState } from "react";
import { AdminLayout } from "../AdminPages";
import { KeyRound, Copy, Check, MessageCircle, Loader2, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => ({ Authorization: `Bearer ${getToken()}` });

export default function AdminStuckResets() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/admin/stuck-password-resets`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const absolute = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    return `${origin}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  const whatsappMessage = (entry) => {
    const url = absolute(entry.magic_url);
    const first = (entry.name || "").split(" ")[0] || "there";
    return `Hi ${first} — Michael here. One-tap sign-in link for Soulumo (expires in 1 hour):\n\n${url}\n\nTap it on your phone and you're in.`;
  };

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch (_) { /* clipboard blocked — link is still visible */ }
  };

  const resolve = async (id) => {
    try {
      await fetch(`${API}/api/auth/admin/stuck-password-resets/${id}/resolve`, { method: "POST", headers: auth() });
      await load();
    } catch (e) { setErr(String(e.message || e)); }
  };

  return (
    <AdminLayout title="Stuck password resets">
      <div className="mb-6" data-testid="admin-stuck-resets-page">
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <KeyRound className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Resend is in sandbox mode</p>
            <p className="mt-1 text-amber-700">
              Password-reset emails to any address except the Resend owner
              drop silently. Users who hit this see &quot;Message Michael on
              WhatsApp&quot;. Below is every tester who tripped — copy the
              one-tap link and paste it into WhatsApp to unblock them.
            </p>
            <p className="mt-2 text-xs text-amber-700/80">
              Fix for good: verify your domain at resend.com/domains and set
              <code className="mx-1 px-1 bg-amber-100 rounded">SENDER_EMAIL</code>
              to an address on it.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : err ? (
        <div className="text-sm text-red-600" data-testid="admin-stuck-error">{err}</div>
      ) : items.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-sm text-slate-500 text-center"
          data-testid="admin-stuck-empty"
        >
          No stuck resets right now. 🎉
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((entry) => {
            const magicUrl = absolute(entry.magic_url);
            const waText = whatsappMessage(entry);
            const waHref = `https://wa.me/?text=${encodeURIComponent(waText)}`;
            return (
              <div
                key={entry.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col gap-3"
                data-testid={`stuck-reset-row-${entry.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {entry.name || entry.email}
                    </div>
                    <div className="text-sm text-slate-500 truncate">{entry.email}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {new Date(entry.created_at).toLocaleString()}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => resolve(entry.id)}
                    aria-label="Mark resolved"
                    className="text-slate-400 hover:text-slate-700 transition"
                    data-testid={`stuck-reset-resolve-${entry.id}`}
                    title="Mark resolved (I've sent them the link)"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs font-mono text-slate-700 overflow-hidden">
                  <span className="truncate flex-1" title={magicUrl}>{magicUrl}</span>
                  <button
                    type="button"
                    onClick={() => copy(magicUrl, `url-${entry.id}`)}
                    className="flex-shrink-0 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-slate-600 hover:text-slate-900"
                    data-testid={`stuck-reset-copy-url-${entry.id}`}
                  >
                    {copiedId === `url-${entry.id}`
                      ? <Check className="w-3.5 h-3.5 text-emerald-500" />
                      : <Copy className="w-3.5 h-3.5" />}
                    Link
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => copy(waText, `wa-${entry.id}`)}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    data-testid={`stuck-reset-copy-message-${entry.id}`}
                  >
                    {copiedId === `wa-${entry.id}`
                      ? <Check className="w-4 h-4 text-emerald-500" />
                      : <Copy className="w-4 h-4" />}
                    Copy WhatsApp message
                  </button>
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-3 py-1.5 text-sm hover:bg-emerald-700"
                    data-testid={`stuck-reset-open-wa-${entry.id}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    Open in WhatsApp
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
